/**
 * /app/me/tasks — Employee Workspace v1 (Task 118).
 *
 * The focused landing surface for agency production-execute staff
 * (designer / senior_designer / video_editor / videographer /
 * content_writer / senior_content_writer). Detected via
 * `isAgencyProductionOnly(me)` — see `apps/web/src/permissions.ts`.
 *
 * Shape:
 *   - GET `/functions/v1/list-my-production-tasks` (user-JWT
 *     PostgREST under existing RLS; no new RPC / migration).
 *   - Groups returned rows by `task.status`:
 *       • جديدة         → status='open' OR 'blocked'
 *       • قيد العمل      → status='in_progress'
 *       • تحتاج تسليم    → status='submitted'
 *   - Each card surfaces: client name, content title, discipline,
 *     assignment source (auto/manual), status pill, and an
 *     "افتح المهمة" CTA that navigates to the client's content
 *     workspace `/app/clients/<id>/content`. v1 lands on the content
 *     list — the user clicks into the specific item from there.
 *     Auto-opening the specific item + scrolling to the production
 *     task panel is a v2 follow-up (will add an optional content-item
 *     query param + ContentPage handler).
 *
 * Empty / loading / error states use the shared `<EmptyState>` /
 * `<LoadingState>` / `<ErrorState>` components for consistency with
 * the rest of the shell.
 *
 * NO mutations. Pure read-only landing page.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMyProductionTasks } from '../api/endpoints';
import type {
  ContentItemStatus,
  ContentDiscipline,
  ListMyProductionTasksData,
  MyProductionTask,
  MyProductionTaskStatus,
} from '../api/types';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { useMeContext } from '../context/MeContextProvider';
import {
  isWritingTask,
  MY_TASKS_INTRO_PRODUCTION_AR,
  MY_TASKS_INTRO_WRITING_AR,
  viewerIsWriterOnly,
  writingTaskHref,
} from './contentWriterWorkspace.logic';
import { filterTasksByClient } from './productionWorkspace.logic';
import { useProductionClientSelection } from './useProductionClientSelection';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tasks: ReadonlyArray<MyProductionTask> }
  | { readonly status: 'error'; readonly error: unknown };

export function EmployeeTasksPage() {
  const { me } = useMeContext();
  const { selectedClientId } = useProductionClientSelection(me?.client_assignments ?? []);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    listMyProductionTasks({ signal: ctrl.signal })
      .then((data: ListMyProductionTasksData) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', tasks: data.tasks });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  if (state.status === 'loading') {
    return <LoadingState title="جاري تحميل مهامك" />;
  }
  if (state.status === 'error') {
    return (
      <ErrorState
        title="تعذّر تحميل المهام"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );
  }

  const tasks = filterTasksByClient(state.tasks, selectedClientId);
  const writerViewer = me ? viewerIsWriterOnly(me) : false;
  const newTasks = tasks.filter((t) => t.task_status === 'open' || t.task_status === 'blocked');
  const inProgressTasks = tasks.filter((t) => t.task_status === 'in_progress');
  const needsSubmissionTasks = tasks.filter((t) => t.task_status === 'submitted');

  return (
    <div className="stack-md">
      <header>
        <h1 className="h-page">مهامي</h1>
        {/* Task 292C1-QA-B — a content writer produces TEXT and has no
            production brief, so «مهام الإنتاج … وموجز الإنتاج» was simply wrong
            for them. Designer / video wording is unchanged. */}
        <p className="body" style={{ marginTop: 8, maxWidth: 680 }}>
          {writerViewer ? MY_TASKS_INTRO_WRITING_AR : MY_TASKS_INTRO_PRODUCTION_AR}
        </p>
      </header>

      {tasks.length === 0 ? (
        selectedClientId ? (
          <EmptyState
            title="لا توجد مهام لهذا العميل"
            description="أنت معيّن على هذا العميل، لكن لا توجد لك مهمة إنتاج حالياً."
          />
        ) : (
          <EmptyState
            title="لا توجد مهام مخصّصة لك حالياً"
            description="ستظهر هنا فور تعيينك على أي مهمة إنتاج — إمّا تلقائياً بعد اعتماد العميل (تعيين تلقائي)، أو يدوياً من قِبَل المالك أو مدير التسويق (تعيين يدوي)."
          />
        )
      ) : (
        <>
          {newTasks.length > 0 ? (
            <TaskGroup title="جديدة" count={newTasks.length} tasks={newTasks} />
          ) : null}
          {inProgressTasks.length > 0 ? (
            <TaskGroup title="قيد العمل" count={inProgressTasks.length} tasks={inProgressTasks} />
          ) : null}
          {needsSubmissionTasks.length > 0 ? (
            <TaskGroup
              title="تحتاج تسليم"
              count={needsSubmissionTasks.length}
              tasks={needsSubmissionTasks}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

interface TaskGroupProps {
  readonly title: string;
  readonly count: number;
  readonly tasks: ReadonlyArray<MyProductionTask>;
}

function TaskGroup({ title, count, tasks }: TaskGroupProps): JSX.Element {
  return (
    <section className="stack-sm">
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h2 className="h-section">{title}</h2>
        <span className="count">{count}</span>
      </header>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))',
          gap: 12,
        }}
      >
        {tasks.map((task) => (
          <TaskCard key={task.assignment_id} task={task} />
        ))}
      </div>
    </section>
  );
}

interface TaskCardProps {
  readonly task: MyProductionTask;
}

function TaskCard({ task }: TaskCardProps): JSX.Element {
  const clientName = task.client?.name ?? 'عميل غير متاح';
  const contentTitle = task.content_item?.title ?? 'مهمة بدون عنوان';
  const discipline = task.content_item?.discipline;
  const assignmentSource = readSource(task.assignment_metadata);
  // Task 292C1-QA-A — a writing task is identified by the TASK's own
  // discipline, carried in `assignment_metadata`. `content_item.discipline`
  // stays `content` for the whole writing lane (immutable by trigger), which is
  // exactly why this card used to label a writing task «محتوى».
  const writing = isWritingTask(task);
  // Task 290A — the v2 deep link promised by tasks/118 §URL contract:
  // `?contentItem=<content_item_id>` is now consumed by ContentPage
  // (selects the exact item after the authorized list loads, then
  // scrolls to its production-task panel once loaded; unknown or
  // inaccessible ids fall back to the default list behavior with no
  // direct fetch). When the content_item embed is null (RLS drift),
  // the link degrades to the plain content workspace exactly as v1.
  // Task 292C1-QA-A — writing tasks get the canonical writer deep link.
  //
  // The production link below is WRONG for them on three counts: it targets the
  // agency content page (which has no writer editor), `?contentItem` is
  // consumed once and stripped from the URL by ContentPage, and the writer
  // workspace had no route at all. `/app/me/writing/<taskId>` addresses the
  // exact task by its STABLE id and survives refresh and back/forward.
  //
  // Designer / video tasks keep the existing production link unchanged.
  const href = writing
    ? writingTaskHref(task.task_id)
    : task.client
      ? task.content_item
        ? `/app/clients/${encodeURIComponent(task.client.id)}/content?contentItem=${encodeURIComponent(task.content_item.id)}`
        : `/app/clients/${encodeURIComponent(task.client.id)}/content`
      : '/app/clients';

  return (
    <article
      className="card"
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div className="stack-xs">
          <span className="lbl">{clientName}</span>
          <h3 className="h-section" style={{ marginTop: 2 }}>
            {contentTitle}
          </h3>
        </div>
        <StatusPill status={task.task_status} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {writing ? (
          <span className="pill pill-info">كتابة</span>
        ) : discipline ? (
          <DisciplinePill discipline={discipline} />
        ) : null}
        <SourcePill source={assignmentSource} />
        {/* Task 292C1-QA-A — the writing lane runs BEFORE client approval, so
            the parent item's own state is the useful context here (a writing
            task spans every platform variant of the item, so there is no single
            platform to show — see the task file for why per-platform context
            would need a `list-my-production-tasks` DTO change). */}
        {writing && task.content_item ? <ItemStatePill status={task.content_item.status} /> : null}
      </div>
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
        <Link to={href} className="btn">
          افتح المهمة
        </Link>
      </div>
    </article>
  );
}

function readSource(
  metadata: Readonly<Record<string, unknown>>,
): 'auto' | 'manual' | 'writing' | 'unknown' {
  const src = metadata['assignment_source'];
  if (src === 'auto_on_handoff') return 'auto';
  if (src === 'manual') return 'manual';
  // Task 292C1-QA-A — `send_content_item_to_writing_v1` stamps this third
  // provenance. Without it the card fell through to «مصدر غير معروف».
  if (src === 'writing_handoff') return 'writing';
  return 'unknown';
}

/**
 * Task 292C1-QA-A — parent item state for a writing task. Only the two
 * pre-client-approval states are reachable: `send_content_item_to_writing_v1`
 * refuses anything from `client_content_approved` onward. Wording matches
 * ContentPage's own status filter so the two surfaces agree.
 */
function ItemStatePill({ status }: { readonly status: ContentItemStatus }): JSX.Element | null {
  if (status === 'draft') return <span className="pill pill-draft">مسودة</span>;
  if (status === 'pending_client_content_approval') {
    return <span className="pill pill-review">مراجعة محتوى</span>;
  }
  return null;
}

function StatusPill({ status }: { readonly status: MyProductionTaskStatus }): JSX.Element {
  switch (status) {
    case 'open':
      return <span className="pill pill-draft">جديدة</span>;
    case 'blocked':
      return <span className="pill pill-review">معلَّقة</span>;
    case 'in_progress':
      return <span className="pill pill-info">قيد العمل</span>;
    case 'submitted':
      return <span className="pill pill-review">قيد المراجعة</span>;
    default:
      return <span className="pill pill-draft">{status}</span>;
  }
}

function DisciplinePill({ discipline }: { readonly discipline: ContentDiscipline }): JSX.Element {
  switch (discipline) {
    case 'design':
      return <span className="pill pill-info">تصميم</span>;
    case 'video':
      return <span className="pill pill-info">فيديو</span>;
    case 'content':
      return <span className="pill pill-info">محتوى</span>;
    default:
      return <span className="pill pill-info">{discipline}</span>;
  }
}

function SourcePill({
  source,
}: {
  readonly source: 'auto' | 'manual' | 'writing' | 'unknown';
}): JSX.Element {
  if (source === 'auto') {
    return <span className="pill pill-active">تعيين تلقائي</span>;
  }
  if (source === 'manual') {
    return <span className="pill pill-active">تعيين يدوي</span>;
  }
  if (source === 'writing') {
    return <span className="pill pill-active">إرسال للكتابة</span>;
  }
  return <span className="pill pill-draft">مصدر غير معروف</span>;
}
